/** @format */

import { usePostHog } from "posthog-js/react";
import { useEffect } from "react";
import { useCTAStore } from "@/stores/ctaStore";
import { usePWAInstallStore } from "@/stores/pwaInstallStore";
import { incrementVisitCount } from "@/utils/visitCounter";

const SHOW_DELAY_MS = 1500;

/**
 * Side-effect hook that wires up PWA install prompt listeners.
 * Call once at the app shell level (LiveWrapper).
 */
export function usePWAInstallPrompt() {
  const posthog = usePostHog();

  useEffect(() => {
    incrementVisitCount();
    // Visit count changed — CTA cooldown may have expired
    useCTAStore.getState().recompute();

    const timer = setTimeout(() => {
      usePWAInstallStore.getState().checkShouldShow();
    }, SHOW_DELAY_MS);

    const handleBeforeInstall = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      usePWAInstallStore.getState().setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstall);

    const handleAppInstalled = () => {
      const { platform } = usePWAInstallStore.getState();
      usePWAInstallStore.getState().clearBanner();
      posthog?.capture("pwa_installed", { platform: platform.type });
    };
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, [posthog]);
}
